import Big from 'big.js'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'
import { useRate } from '@/pages/Donation/useRate'
import { convert } from '@/pages/Donation/utils'

export type MediaPriceLimit = {
  payAmount: number
  totalMinAmount: number
}

export type MediaPricesLimits = {
  image?: MediaPriceLimit
  video?: MediaPriceLimit
  audio?: MediaPriceLimit
}

export const getAmountToPay = (amount: string | null, minAmount: number | null) => {
  const isNeedToPay = minAmount != null && minAmount > Number(amount)

  if (!isNeedToPay) {
    return
  }

  const payAmount = new Big(minAmount).sub(amount || 0)
  const totalMinAmount = payAmount.add(amount || 0)

  return {
    payAmount: payAmount.toNumber(),
    totalMinAmount: totalMinAmount.toNumber(),
  }
}

type Props = {
  fiatAmount: string | null
  baseCurrencyCode?: string
}

export const useMediaPricesLimits = ({ baseCurrencyCode, fiatAmount }: Props): MediaPricesLimits => {
  const { config } = useStreamerConfigContext()
  const streamerCurrency = config?.base_currency ?? baseCurrencyCode
  const imageMinAmount = config?.image_donation_config?.min_amount
  const videoMinAmount = config?.video_donation_config?.min_amount
  const audioMinAmount = config?.audio_donation_config?.min_amount

  const { rate } = useRate({ fromCode: streamerCurrency, toCode: baseCurrencyCode })

  if (rate == null) {
    return {}
  }

  const convertedImageMinAmount = convert({ amount: imageMinAmount, rate })
  const convertedVideoMinAmount = convert({ amount: videoMinAmount, rate })
  const convertedAudioMinAmount = convert({ amount: audioMinAmount, rate })

  return {
    image: getAmountToPay(fiatAmount, convertedImageMinAmount),
    video: getAmountToPay(fiatAmount, convertedVideoMinAmount),
    audio: getAmountToPay(fiatAmount, convertedAudioMinAmount),
  }
}
